/* eslint-disable camelcase */
import { useState, useEffect, useContext } from "react"
import Header from "../../../layouts/Header"
import BackIcon from "../../../components/BackIcon"
import { useNavigate } from "react-router-dom"
import {
  Box,
  Paper,
  TextField,
  Tooltip,
  IconButton,
  Grid,
  Typography,
  Autocomplete,
  Badge,
  Button,
} from "@mui/material"
import { createTheme, ThemeProvider } from "@mui/material/styles"
import CustomBackdrop from "../../../components/CustomBackdrop"
import { useMutation, useQuery, api, showWarning } from "../../../api"
import ShoppingCartIcon from "@mui/icons-material/ShoppingCart"
import StorefrontIcon from "@mui/icons-material/Storefront"
import { GlobalContext } from "../../../contexts/GlobalContext"
import { useQueryClient } from "@tanstack/react-query"
import Swal from "sweetalert2"

// Importaciones para generación del PDF en Frontend
import JsPDF from "jspdf"
import autoTable from "jspdf-autotable"
import dayjs from "dayjs"

import CatalogoProductos from "./components/CatalogoProductos"
import CarritoResumen from "./components/CarritoResumen"

const theme = createTheme({
  palette: { primary: { main: "#196C87" }, secondary: { main: "#2E7D32" } },
})

const StyledRoot = {
  width: "100%",
  maxWidth: "1400px",
  margin: "0 auto",
  padding: "20px",
  backgroundColor: "#f5f7fa",
  borderRadius: "12px",
  boxShadow: "0 4px 20px rgba(0,0,0,0.1)",
}

const CrearSolicitudIngresoCliente = () => {
  const navigate = useNavigate()
  const queryClient = useQueryClient()

  // Extraemos TODO el contexto global para buscar dinámicamente el nombre de la compañía
  const globalContext = useContext(GlobalContext)

  const [carritoOpen, setCarritoOpen] = useState(false)

  // Obtenemos la fecha actual en formato YYYY-MM-DD para inicializar el campo
  const fechaHoy = new Date().toISOString().split("T")[0]

  // Paso 1: Estado unificado
  const [formData, setFormData] = useState({
    tsolcodigo: "",
    tsolInEg: "",
    ciaaproautinsol: 0,
    motcodigo: "",
    sgadescri: "",
    sgacomenllegada: "",
    clicodigo: "",
    procodigo: "",
    sgafecllegada: fechaHoy,
    sgahorllegada: "",
    detalle: [],
  })

  // CARGA DE CATÁLOGOS
  const { data: catData, isLoading: loadCat } = useQuery({
    queryKey: ["catalogosIngresoCliente"],
    queryFn: async () => {
      try {
        const response = await api.post("/CrearSolicitudIngCli/getCatalogosIngreso", {})

        let catalogos = {}
        const res = response?.data?.data || response?.data || response || {}

        if (res.catalogo_productos) {
          catalogos = res
        } else if (res.data && res.data.catalogo_productos) {
          catalogos = res.data
        }

        return catalogos
      } catch (error) {
        return { tipos_solicitud: [], motivos: [], catalogo_productos: [], proveedores: [], cliente_asignado: null }
      }
    },
    staleTime: 0,
  })

  // Paso 2: Extracción Defensiva de arreglos
  const extractArray = (rawList) =>
    Array.isArray(rawList) ? rawList : Array.isArray(rawList?.data) ? rawList.data : []

  const listaTiposSolicitud = extractArray(catData?.tipos_solicitud)
  const listaMotivos = extractArray(catData?.motivos)
  const listaProveedores = extractArray(catData?.proveedores)
  const catalogoProductos = extractArray(catData?.catalogo_productos)
  const clienteAsignado = catData?.cliente_asignado || null

  // Sincronizar el clicodigo validado por el backend
  useEffect(() => {
    if (clienteAsignado?.id && formData.clicodigo !== clienteAsignado.id) {
      setFormData((prev) => ({ ...prev, clicodigo: clienteAsignado.id }))
    }
  }, [clienteAsignado, formData.clicodigo])

  // ==========================================================================
  // GENERACIÓN DE REPORTE PDF (LOCAL) CON EXTRACCIÓN DINÁMICA Y ALINEACIÓN
  // ==========================================================================
  const handleImprimirReporte = (solnumero) => {
    const doc = new JsPDF("p", "pt", "a4")

    // 1. Extracción dinámica del nombre de la compañía desde las variables globales (GlobalContext)
    const nombreCiaDinamic =
      globalContext?.ciaSeleccionada?.label ||
      globalContext?.ciaSeleccionada?.cianombre ||
      globalContext?.empresaSeleccionada?.label ||
      globalContext?.seleccion?.cianombre ||
      "SISTEMA LOGÍSTICO MERCATTI"

    // Buscamos los descriptivos para mostrar en el PDF
    const tipoSol = listaTiposSolicitud.find((t) => t.tsolcodigo === formData.tsolcodigo)?.label || ""
    const motivo = listaMotivos.find((m) => m.id === formData.motcodigo)?.label || ""
    const proveedor = listaProveedores.find((p) => p.id === formData.procodigo)?.label || "N/A"

    // Helper para truncar textos muy largos y evitar que choquen (Max 65 caracteres)
    const truncateTxt = (txt, max) => (txt.length > max ? txt.substring(0, max) + "..." : txt)

    // --- ENCABEZADO INSTITUCIONAL ---
    doc.setFontSize(16)
    doc.setTextColor(25, 108, 135) // #196C87
    doc.text(nombreCiaDinamic, 40, 40)

    doc.setFontSize(14)
    doc.setTextColor(0, 0, 0)
    // Se inserta correctamente el número de solicitud
    doc.text(`SOLICITUD DE INGRESO NRO: ${solnumero}`, 40, 65)

    // --- DATOS DE CABECERA ---
    doc.setFontSize(10)
    doc.setTextColor(60, 60, 60)

    // Textos alineados a la izquierda (Truncados a 70 caracteres máximo para no chocar)
    doc.text(truncateTxt(`Cliente Asociado: ${clienteAsignado?.label || formData.clicodigo}`, 70), 40, 95)
    doc.text(truncateTxt(`Tipo de Solicitud: ${tipoSol}`, 70), 40, 110)
    doc.text(truncateTxt(`Motivo de Ingreso: ${motivo}`, 70), 40, 125)
    doc.text(truncateTxt(`Proveedor Remitente: ${proveedor}`, 70), 40, 140)

    // Textos alineados a la DERECHA (X = 555) para evitar sobreposición absoluta
    doc.text(
      `Fecha/Hora Llegada: ${dayjs(formData.sgafecllegada).format("DD/MM/YYYY")} a las ${formData.sgahorllegada}`,
      555,
      95,
      { align: "right" },
    )
    doc.text(`Fecha Impresión: ${dayjs().format("DD/MM/YYYY HH:mm")}`, 555, 110, { align: "right" })

    // Descripción General (Manejo de saltos de línea)
    doc.setFont("helvetica", "bold")
    doc.text("Descripción General:", 40, 160)
    doc.setFont("helvetica", "normal")
    const splitDesc = doc.splitTextToSize(formData.sgadescri, 500)
    doc.text(splitDesc, 40, 175)

    // --- TABLA DE DETALLE (EL CARRITO) ---
    const tableColumn = ["Ítem", "Código", "Descripción", "Cantidad Solicitada"]
    const tableRows = []

    formData.detalle.forEach((prod, idx) => {
      tableRows.push([idx + 1, prod.artcodigo, prod.artdescri, prod.cantidad_solicitada])
    })

    autoTable(doc, {
      startY: 190 + splitDesc.length * 10,
      head: [tableColumn],
      body: tableRows,
      theme: "plain",
      styles: { fontSize: 9, cellPadding: 4, textColor: [40, 40, 40] },
      headStyles: { fillColor: [25, 108, 135], textColor: [255, 255, 255], fontStyle: "bold" },
      alternateRowStyles: { fillColor: [250, 250, 250] },
      columnStyles: {
        0: { halign: "center", cellWidth: 40 },
        1: { halign: "left", cellWidth: 100 },
        2: { halign: "left" },
        3: { halign: "center", cellWidth: 100, fontStyle: "bold" },
      },
    })

    doc.save(`Solicitud_Ingreso_${solnumero}.pdf`)
  }

  // Mutación de guardado
  const { mutateAsync: SaveSolicitud, isPending: isSaving } = useMutation({
    queryKey: ["isCreatingSolIngCli"],
    fn: async (data) => {
      const response = await api.post("/CrearSolicitudIngCli/createSolicitudIngreso", data)
      return response.data
    },
    showError: "modal",
    showSuccess: false,
    onSuccess: async (response) => {
      await queryClient.invalidateQueries()

      // Extracción SEGURA del solnumero cuando viene empaquetado por el decorador @api_endpoint de Flask
      const numeroGenerado = response?.data?.solnumero || response?.solnumero || ""

      const confirmacion = await Swal.fire({
        title: "¡Solicitud Registrada!",
        html: `<p>Se ha generado exitosamente la Solicitud Nro: <strong>${numeroGenerado}</strong></p><p>¿Desea imprimir el comprobante?</p>`,
        icon: "success",
        showCancelButton: true,
        confirmButtonText: "Sí, imprimir",
        cancelButtonText: "No, salir",
        confirmButtonColor: "#196C87",
        cancelButtonColor: "#7f8c8d",
        reverseButtons: true,
        allowOutsideClick: false,
      })

      if (confirmacion.isConfirmed) {
        handleImprimirReporte(numeroGenerado)
        navigate(-1)
      } else {
        navigate(-1)
      }
    },
  })

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value.toUpperCase() }))
  }

  const handleDateChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }))
  }

  const handleTipoSolicitudChange = (event, newValue) => {
    const nuevoTipo = newValue ? String(newValue.id) : ""
    const nuevoCodigo = newValue ? String(newValue.tsolcodigo) : ""
    const autoAprob = newValue?.ciaaproautinsol || 0

    setFormData((prev) => ({
      ...prev,
      tsolInEg: nuevoTipo,
      tsolcodigo: nuevoCodigo,
      ciaaproautinsol: autoAprob,
    }))
  }

  const handleCheckout = async () => {
    if (!formData.tsolcodigo) return showWarning("Debe seleccionar un Tipo de Solicitud permitido.")
    if (!formData.motcodigo) return showWarning("Debe seleccionar un Motivo para su ingreso.")
    if (!formData.sgafecllegada) return showWarning("Debe especificar la Fecha estimada de llegada.")
    if (!formData.sgahorllegada) return showWarning("Debe especificar la Hora estimada de llegada.")
    if (!formData.sgadescri.trim())
      return showWarning("Por favor, agregue una descripción u observación a su solicitud.")
    if (formData.detalle.length === 0) return showWarning("Su carrito de solicitud está vacío.")

    const carritoLimpio = formData.detalle.filter((item) => parseFloat(item.cantidad_solicitada) > 0)

    if (carritoLimpio.length === 0) {
      return showWarning("Todos los productos en su carrito tienen cantidad 0. Ajuste las cantidades.")
    }

    const payload = { ...formData, detalle: carritoLimpio }
    await SaveSolicitud(payload)
  }

  const loadingGlobal = loadCat || isSaving
  const totalItemsEnCarrito = formData.detalle.reduce(
    (acc, item) => acc + (parseFloat(item.cantidad_solicitada) > 0 ? 1 : 0),
    0,
  )

  return (
    <ThemeProvider theme={theme}>
      <Header />
      <div className="main main-app p-3 p-lg-4">
        <BackIcon />

        <Box sx={{ mb: 2, display: "flex", justifyContent: "flex-end" }}>
          <Button
            variant="contained"
            color="primary"
            size="large"
            startIcon={
              <Badge badgeContent={totalItemsEnCarrito} color="error">
                <ShoppingCartIcon />
              </Badge>
            }
            onClick={() => setCarritoOpen(true)}
            disabled={loadingGlobal}
            sx={{ fontWeight: "bold", borderRadius: 8, px: 4 }}
          >
            Ver Mi Solicitud
          </Button>
        </Box>

        <div style={{ display: "flex", justifyContent: "center", alignItems: "center", margin: "0 30px 30px 30px" }}>
          <StorefrontIcon sx={{ fontSize: 40, color: "#196C87", mr: 1 }} />
          <Typography variant="h4" color="primary" fontWeight="bold">
            Crear Solicitud de Ingreso
          </Typography>
        </div>

        <CustomBackdrop isLoading={loadingGlobal} />

        <Box sx={StyledRoot}>
          <Paper elevation={2} sx={{ p: 4, borderRadius: 3, background: "white", mb: 4 }}>
            <Typography
              variant="h6"
              color="primary"
              gutterBottom
              sx={{ borderBottom: "2px solid #196C87", pb: 1, mb: 3 }}
            >
              Información de Ingreso
            </Typography>
            <Grid container spacing={3}>
              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  label="Cliente Asociado *"
                  value={clienteAsignado ? clienteAsignado.label : "Cargando..."}
                  InputProps={{ readOnly: true }}
                  InputLabelProps={{ shrink: true }}
                  disabled
                />
              </Grid>

              <Grid item xs={12} sm={4}>
                <Autocomplete
                  disabled={loadingGlobal || listaTiposSolicitud.length === 0}
                  options={listaTiposSolicitud}
                  getOptionLabel={(option) => option?.label || ""}
                  value={
                    listaTiposSolicitud.find((c) => String(c.tsolcodigo) === String(formData.tsolcodigo)) ||
                    (formData.tsolcodigo
                      ? { id: formData.tsolInEg, tsolcodigo: formData.tsolcodigo, label: "Cargando..." }
                      : null)
                  }
                  isOptionEqualToValue={(option, value) => String(option.tsolcodigo) === String(value?.tsolcodigo)}
                  onChange={handleTipoSolicitudChange}
                  renderInput={(params) => (
                    <TextField {...params} label="Tipo de Solicitud *" InputLabelProps={{ shrink: true }} />
                  )}
                />
              </Grid>

              <Grid item xs={12} sm={4}>
                <Autocomplete
                  disabled={loadingGlobal || listaProveedores.length === 0}
                  options={listaProveedores}
                  getOptionLabel={(option) => option?.label || ""}
                  value={listaProveedores.find((p) => String(p.id) === String(formData.procodigo)) || null}
                  isOptionEqualToValue={(option, value) => String(option.id) === String(value?.id)}
                  onChange={(e, newVal) =>
                    setFormData((prev) => ({ ...prev, procodigo: newVal ? String(newVal.id) : "" }))
                  }
                  renderInput={(params) => (
                    <TextField {...params} label="Proveedor (Opcional)" InputLabelProps={{ shrink: true }} />
                  )}
                />
              </Grid>

              <Grid item xs={12} sm={4}>
                <Autocomplete
                  disabled={loadingGlobal}
                  options={listaMotivos}
                  getOptionLabel={(option) => option?.label || ""}
                  value={listaMotivos.find((c) => String(c.id) === String(formData.motcodigo)) || null}
                  isOptionEqualToValue={(option, value) => String(option.id) === String(value?.id)}
                  onChange={(e, newVal) =>
                    setFormData((prev) => ({ ...prev, motcodigo: newVal ? String(newVal.id) : "" }))
                  }
                  renderInput={(params) => (
                    <TextField {...params} label="Motivo de Ingreso *" InputLabelProps={{ shrink: true }} />
                  )}
                />
              </Grid>

              <Grid item xs={12} sm={2}>
                <TextField
                  fullWidth
                  type="date"
                  label="Fecha de Llegada *"
                  InputLabelProps={{ shrink: true }}
                  inputProps={{ lang: "es-EC" }}
                  value={formData.sgafecllegada}
                  onChange={(e) => handleDateChange("sgafecllegada", e.target.value)}
                  disabled={loadingGlobal}
                />
              </Grid>

              <Grid item xs={12} sm={2}>
                <TextField
                  fullWidth
                  type="time"
                  label="Hora de Llegada *"
                  InputLabelProps={{ shrink: true }}
                  value={formData.sgahorllegada}
                  onChange={(e) => handleDateChange("sgahorllegada", e.target.value)}
                  disabled={loadingGlobal}
                />
              </Grid>

              <Grid item xs={12} sm={4}>
                <TextField
                  disabled={loadingGlobal}
                  fullWidth
                  label="Descripción / Observación *"
                  value={formData.sgadescri}
                  onChange={(e) => handleInputChange("sgadescri", e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  inputProps={{ maxLength: 250 }}
                  placeholder="Ej. Devolución de campaña..."
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  disabled={loadingGlobal}
                  fullWidth
                  label="Instrucciones para la Recepción (Opcional)"
                  value={formData.sgacomenllegada}
                  onChange={(e) => handleInputChange("sgacomenllegada", e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  inputProps={{ maxLength: 250 }}
                  placeholder="Ej. Entregar cajas por la puerta 2..."
                />
              </Grid>
            </Grid>
          </Paper>

          <CatalogoProductos
            catalogo={catalogoProductos}
            carrito={formData.detalle}
            setFormData={setFormData}
            loading={loadCat}
          />
        </Box>
      </div>

      <CarritoResumen
        open={carritoOpen}
        onClose={() => setCarritoOpen(false)}
        carrito={formData.detalle}
        setFormData={setFormData}
        onCheckout={handleCheckout}
        isSaving={isSaving}
      />
    </ThemeProvider>
  )
}

export default CrearSolicitudIngresoCliente
